/**
 * PATH: packages/tenant-shared/src/stores/notificationStore.ts
 * FILE_ID: TENANTSHARED-STORES-NOTIFICATIONS-001
 * PURPOSE: Tenant-agnostic Notification Center store. Single source of
 *          truth for the bell + toast surfaces in every Quotech tenant
 *          SPA (newmark, sam, future tenants).
 * WHY:     Pre-MR-N this store existed as a near-twin pair under
 *          apps/{newmark,sam}/src/shared/stores/notificationStore.ts.
 *          The newmark copy had the BUG-0012 tenant-namespace fix
 *          (VITE_NOTIFICATION_NS env var) and the tenant-event-name
 *          indirection (tenantEventName('notify')) so the SAM and
 *          Newmark bells couldn't trample each other; the SAM copy
 *          still hard-coded `'sam.notifications.v1'` + `'sam.notify'`
 *          and was therefore the source of the cross-tenant leakage
 *          Mark Newman reported on 2026-05-11. Lifting the store here
 *          means SAM gets the BUG-0012 fix automatically through the
 *          shim, and any future tenant inherits it without an extra
 *          port.
 *
 * FLOW (UNCHANGED from pre-lift behaviour):
 *   ┌──────────────┐   ┌─────────────────┐   ┌──────────────────┐
 *   │ pushNotification│ → │ in-memory state │ → │ subscribers + LS │
 *   └──────────────┘   └─────────────────┘   └──────────────────┘
 *                              │
 *                              ↓
 *                     window CustomEvent
 *                     (NOTIFY_EVENT bus)
 *
 * One canonical place where every "something happened" event in the
 * app gets recorded:
 *   - Xero OAuth callback succeeded → "Xero connected · 2 accounts"
 *   - Sync primary now finished     → "Xero sync — 57 accounts, 59 contacts…"
 *   - Contact import completed      → "Imported 432 contacts from Xero"
 *   - Contact migration completed   → "Migrated 19 contacts → SAM Pty Ltd"
 *   - Email classifier rerun        → "Reclassified 143 emails into kanban"
 *   - Background failure            → "Failed to refresh Xero token (will retry)"
 *
 * The store powers TWO surfaces:
 *   1. The top-nav bell button (``Header.tsx``) — shows an unread badge
 *      and opens a dropdown listing recent notifications.
 *   2. The bottom-right toast surface (``Toast.tsx``) — every push to
 *      the store ALSO emits an ephemeral toast.
 *
 * Both surfaces are deliberately fed from the SAME helper —
 * ``notify(...)``. Don't call ``toast()`` directly anymore for things
 * the user might want to revisit; use ``notify()`` so it lands in the
 * bell as well.
 *
 * Persistence: notifications are kept in localStorage so refreshing
 * the SPA doesn't lose context (esp. relevant after a Xero OAuth
 * round-trip that navigates the tab). Capped at MAX_RETAINED to
 * bound localStorage usage; older entries are dropped silently.
 *
 * DEPENDENCIES:
 *   - ../utils/tenantStorage — for `tenantEventName()` (BUG-0012 fix).
 *
 * RELATED:
 *   - apps/{newmark,sam}/src/shared/stores/notificationStore.ts —
 *     thin re-export shims so existing imports still resolve.
 *   - The PdfEditor `notify()` port (separate MR) — first SAM consumer
 *     that finally calls into `notify()` instead of raw `'sam.notify'`.
 */

import { useSyncExternalStore } from 'react'
import { tlsKey, tenantEventName } from '../utils/tenantStorage'

export type NotificationKind = 'success' | 'error' | 'warning' | 'info'

export interface AppNotification {
  /** Stable random ID — survives reloads via localStorage. */
  id: string
  /** Short headline shown in bold (≤80 chars). */
  title: string
  /** Optional body — additional context, can wrap multiple lines. */
  body?: string
  /** Severity — drives icon + colour. */
  kind: NotificationKind
  /** Where the notification came from (xero, kanban, email, ai, …). */
  source: string
  /** ISO 8601 UTC timestamp. */
  createdAt: string
  /** Has the user seen this in the bell dropdown? */
  read: boolean
  /**
   * Optional "Open" action — when set, the bell row gets a clickable
   * button that fires this. Use for "View Xero settings", "Open the
   * imported contacts table", etc. Keep handlers serialisable-friendly
   * (no closures over component state) since the store is persisted.
   */
  cta?: {
    label: string
    /** Either a route to navigate to or a custom action key the
     *  notification consumer knows how to dispatch. */
    href?: string
    actionKey?: string
  }
}

interface NotificationState {
  notifications: AppNotification[]
  unreadCount: number
}

/**
 * BUG-0012 — tenant-scoped localStorage namespace.
 *
 * SAM and Newmark both live on `dashboard.quotech.ai` (different
 * paths, same origin), so a hardcoded `sam.notifications.v1` key
 * leaked SAM's notification bell entries into Newmark's bell when
 * the same browser session opened both. Mark Newman reported on
 * 2026-05-11 that his Newmark dashboard was full of "New SAM version
 * available" rows pointing at SAM commits.
 *
 * The fix derives the namespace from `VITE_NOTIFICATION_NS` (set in
 * each tenant's `.env.production`). SAM keeps `sam.*` for back-compat
 * (no env var needed → default). Newmark sets `newmark` so its bell
 * is fully isolated.
 */
// Delegated to tenantStorage's single namespace resolver (same
// VITE_NOTIFICATION_NS → 'sam' fallback chain) so the store and every
// other tenant-scoped key derive from ONE source of truth.
const STORAGE_KEY = tlsKey('notifications.v1')
const MAX_RETAINED = 50

function loadFromStorage(): AppNotification[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isValidNotification).slice(0, MAX_RETAINED)
  } catch {
    return []
  }
}

function isValidNotification(n: unknown): n is AppNotification {
  if (typeof n !== 'object' || n === null) return false
  const obj = n as Record<string, unknown>
  // ``body`` is OPTIONAL but if present MUST be a string. A previous
  // bug let an object body slip into localStorage; that row then
  // crashed the bell with React #31 (Objects are not valid as a
  // React child) and blanked the whole app for the affected user
  // (jake 2026-05-04, "tried to connect outlook from the new team
  // account → blank screen"). Filter at load-time so even users on
  // poisoned storage recover on the next refresh.
  if ('body' in obj && obj.body !== undefined && typeof obj.body !== 'string') {
    return false
  }
  return (
    typeof obj.id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.kind === 'string' &&
    typeof obj.createdAt === 'string'
  )
}

function persist(state: NotificationState) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.notifications))
  } catch {
    /* private mode / quota — ignore */
  }
}

// ---------------------------------------------------------------------------
// Manual store (no zustand dependency required for this — keeps bundle
// lean and avoids the weight of a fully reactive store for a list that
// only mutates on a handful of events per session).
// ---------------------------------------------------------------------------

let state: NotificationState = (() => {
  const initial = loadFromStorage()
  return {
    notifications: initial,
    unreadCount: initial.filter((n) => !n.read).length,
  }
})()

const subscribers = new Set<() => void>()

function setState(next: NotificationState) {
  state = next
  persist(next)
  subscribers.forEach((cb) => cb())
}

function subscribe(callback: () => void): () => void {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

function getSnapshot(): NotificationState {
  return state
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Push a new notification. Returns the assigned ID so callers can
 * dismiss it later (e.g. a "Sync running…" notification that gets
 * replaced by "Sync complete" when it finishes).
 */
export function pushNotification(input: Omit<AppNotification, 'id' | 'createdAt' | 'read'>): string {
  const n: AppNotification = {
    ...input,
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `n_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    createdAt: new Date().toISOString(),
    read: false,
  }
  const trimmed = [n, ...state.notifications].slice(0, MAX_RETAINED)
  setState({
    notifications: trimmed,
    unreadCount: trimmed.filter((x) => !x.read).length,
  })
  return n.id
}

export function dismissNotification(id: string) {
  const trimmed = state.notifications.filter((n) => n.id !== id)
  setState({
    notifications: trimmed,
    unreadCount: trimmed.filter((x) => !x.read).length,
  })
}

export function markNotificationRead(id: string) {
  const updated = state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
  setState({
    notifications: updated,
    unreadCount: updated.filter((x) => !x.read).length,
  })
}

export function markAllNotificationsRead() {
  const updated = state.notifications.map((n) => ({ ...n, read: true }))
  setState({
    notifications: updated,
    unreadCount: 0,
  })
}

export function clearAllNotifications() {
  setState({ notifications: [], unreadCount: 0 })
}

/** Replace an existing notification's title/body/kind. Returns true on hit. */
export function updateNotification(
  id: string,
  patch: Partial<Omit<AppNotification, 'id' | 'createdAt'>>,
): boolean {
  let hit = false
  const updated = state.notifications.map((n) => {
    if (n.id !== id) return n
    hit = true
    return { ...n, ...patch }
  })
  if (!hit) return false
  setState({
    notifications: updated,
    unreadCount: updated.filter((x) => !x.read).length,
  })
  return true
}

// ---------------------------------------------------------------------------
// React hook + cross-tab + cross-module event bus
// ---------------------------------------------------------------------------

/** React hook — re-renders when the notification list changes. */
export function useNotifications(): NotificationState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Cross-module event bus — any code in the app (including non-React
 * modules like ``useXeroStatus.ts``) can dispatch notifications by
 * firing the ``${TENANT_NAMESPACE}.notify`` window event with an
 * ``AppNotification`` detail. Keeps the store free of tight coupling
 * to feature modules.
 *
 * 2026-05-12 — was hard-coded to `'sam.notify'`. With SAM and
 * Newmark sharing the same browser tab origin, an `sam.notify`
 * dispatched from Newmark would surface a toast in any open SAM
 * tab. Now derives the event name from `tenantEventName('notify')`
 * so each tenant's bus is isolated.
 *
 * Example (tenant-agnostic — always import the constant):
 *   import { NOTIFY_EVENT, notify } from '@quotech/tenant-shared/stores/notificationStore'
 *   window.dispatchEvent(new CustomEvent(NOTIFY_EVENT, {
 *     detail: { title: 'Xero connected', kind: 'success', source: 'xero' },
 *   }))
 *   // …or just call `notify({ title: …, kind: …, source: … })`.
 */
export const NOTIFY_EVENT = tenantEventName('notify')

if (typeof window !== 'undefined') {
  const w = window as Window & { __samNotifyBusInstalled?: boolean }
  if (!w.__samNotifyBusInstalled) {
    w.__samNotifyBusInstalled = true
    window.addEventListener(NOTIFY_EVENT, (e) => {
      const detail = (e as CustomEvent).detail as
        | Omit<AppNotification, 'id' | 'createdAt' | 'read'>
        | undefined
      if (!detail || typeof detail.title !== 'string') return
      pushNotification(detail)
    })

    // Cross-tab sync — when localStorage changes in another tab, refresh
    // local state. Useful after the Xero OAuth tab closes and the
    // notification was written there.
    window.addEventListener('storage', (e) => {
      if (e.key !== STORAGE_KEY) return
      const refreshed = loadFromStorage()
      setState({
        notifications: refreshed,
        unreadCount: refreshed.filter((n) => !n.read).length,
      })
    })
  }
}

/**
 * Convenience helper — dispatch a notification from anywhere (React or
 * not) without importing the store directly.
 */
export function notify(detail: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(NOTIFY_EVENT, { detail }))
}
